import React, { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

import { useSourceContext } from "../../contexts/SourcesContext";
import { sourcesPanelStyles } from "../PanelStyles";

import SourcesHeader from "./SourcesHeader";
import SourceTypeToggle from "./SourceTypeToggle";
import SourceCard from "./SourceCard";
import { Source, SourceType } from "../../types/frontend";

/**
 * Props for the SourcesPanel component.
 */
interface SourcesPanelProps {
  isPanelExpanded: boolean; // Indicates if the panel is expanded
  setIsPanelExpanded: React.Dispatch<React.SetStateAction<boolean>>; // Function to toggle panel expansion
}

/**
 * SourcesPanel Component
 *
 * Renders a panel for managing and displaying sources with options to toggle source types
 * and attach/detach sources.
 *
 * @param {SourcesPanelProps} props - Component props
 * @returns {JSX.Element} Rendered SourcesPanel component
 */
const SourcesPanel: React.FC<SourcesPanelProps> = ({
  isPanelExpanded,
  setIsPanelExpanded,
}) => {
  // Access source context values
  const {
    invertedSources,
    sourceType,
    setSourceType,
    attachedSources,
    setAttachedSources,
  } = useSourceContext();
  const [isLoading] = useState(false); // State to manage loading status
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null); // State to track expanded source card

  /**
   * Handle the change event for the source type toggle.
   * Updates the source type in the context.
   *
   * @param {React.MouseEvent<HTMLElement>} event - The mouse event
   * @param {SourceType | null} newValue - The new source type value
   */
  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: SourceType | null
  ) => {
    if (newValue !== null) {
      setSourceType(newValue);
    }
  };

  /**
   * Handle the click event for a source card.
   * Toggles the expansion state of the source card.
   *
   * @param {any} source - The source object
   */
  const handleSourceClick = (source: any) => {
    if (expandedCardId === source.id) {
      setExpandedCardId(null);
      setIsPanelExpanded(false);
    } else {
      setExpandedCardId(source.id);
      setIsPanelExpanded(true);
    }
  };

  /**
   * Handle the click event for the checkbox in a source card.
   * Toggles the attachment state of the source.
   *
   * @param {Source} source - The source object
   * @param {React.MouseEvent} e - The mouse event
   */
  const onCheckboxClick = (source: Source, e: React.MouseEvent) => {
    e.stopPropagation();
    // If the source is already attached, remove it
    if (attachedSources.includes(source)) {
      handleRemoveSource(source);
    } else {
      setAttachedSources((sources) => [...sources, source]);
    }
  };

  /**
   * Remove a source from the attached sources list.
   *
   * @param {Source} source - The source object to remove
   */
  const handleRemoveSource = (source: Source) => {
    setAttachedSources((sources) => sources.filter((s) => s.id !== source.id));
  };

  /**
   * Handle the unexpand action for the panel.
   * Resets the expanded card state and collapses the panel.
   */
  const handleUnexpand = () => {
    setExpandedCardId(null);
    setIsPanelExpanded(false);
  };

  return (
    <Box sx={sourcesPanelStyles.container}>
      <SourcesHeader
        isPanelExpanded={isPanelExpanded}
        handleUnexpand={handleUnexpand}
      />
      <SourceTypeToggle sourceType={sourceType} onChange={handleToggleChange} />
      <Box sx={sourcesPanelStyles.body}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : invertedSources.length === 0 ? (
          <Typography sx={sourcesPanelStyles.noSourcesText}>
            Select your source preference and start a chat.
          </Typography>
        ) : (
          invertedSources.map((source, index) => (
            <SourceCard
              key={source.id || `${source.id}-${index}`}
              source={source}
              isAttached={attachedSources.includes(source)}
              onClick={() => handleSourceClick(source)}
              onCheckboxClick={(e) => onCheckboxClick(source, e)}
              expandedCardId={expandedCardId}
              isPanelExpanded={isPanelExpanded}
              index={index}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default SourcesPanel;
